---
meta:
  - title: Qué son las instrucciones de Auri - IUSPRO Docs
  - description: Entienda qué son las instrucciones, para qué sirven y en qué casos conviene usarlas.
---

# Qué son las instrucciones de Auri

Una **instrucción** es una guía reutilizable que le dice a Auri **cómo analizar** y **cómo responder**.

Sirve para no tener que escribir las mismas indicaciones una y otra vez en cada chat.

Piense en esto:

- su consulta cambia de un asunto a otro
- pero su forma de trabajar muchas veces se repite
- la instrucción guarda esa forma de trabajar

Así, cuando la selecciona, Auri ya sabe qué enfoque seguir antes de responder.

## ¿Para qué sirve?

Sirve para mantener un criterio estable en tareas que usted hace con frecuencia.

Por ejemplo, puede usar una instrucción para pedirle a Auri que:

- responda con un enfoque más conservador y cauteloso
- priorice riesgos procesales antes de dar una conclusión
- organice la respuesta en hechos, norma, análisis y recomendación
- redacte con tono ejecutivo para clientes
- se concentre en encontrar vacíos, debilidades o contraargumentos

## Un ejemplo fácil de entender

Imagine que usted suele analizar conflictos laborales.

Sin una instrucción, en cada chat tendría que repetir algo como:

"Analice primero los hechos, luego identifique riesgos, después posibles defensas, y cierre con una recomendación breve y práctica."

Con una instrucción, eso se guarda una sola vez.

Después, en cada chat nuevo, solo la selecciona y Auri trabaja con ese mismo enfoque.

## Qué gana al usar instrucciones

- **Ahorra tiempo** porque evita repetir indicaciones largas
- **Mantiene consistencia** entre chats parecidos
- **Estandariza criterio** dentro de su firma o equipo
- **Mejora el punto de partida** de cada conversación con Auri

## Qué no es una instrucción

Esto es importante porque en IUSPRO hay varias herramientas parecidas, pero no sirven para lo mismo.

- **Instrucciones**: guían cómo Auri analiza y responde
- **Formatos**: guían cómo Redactor redacta documentos
- **Manuales**: guían cómo Revisión evalúa documentos

En otras palabras:

- si quiere cambiar la forma en que Auri piensa o estructura una respuesta, use una **instrucción**
- si quiere cambiar la forma en que se redacta un escrito, use un **formato**
- si quiere revisar si un documento cumple ciertos criterios, use un **manual**

## Cuándo conviene crear una instrucción

Conviene crearla cuando usted nota que siempre le pide a Auri el mismo tipo de comportamiento.

Por ejemplo:

- siempre quiere respuestas cortas y ejecutivas
- siempre quiere que detecte riesgos antes de opinar
- siempre quiere que compare dos posibles estrategias
- siempre quiere que se enfoque en jurisprudencia antes que en doctrina
- siempre quiere una estructura fija en la respuesta

Si esa preferencia se repite, conviene convertirla en instrucción.

## Ejemplos útiles

### 1. Instrucción para análisis conservador

Útil cuando quiere que Auri evite conclusiones apresuradas.

Puede indicar cosas como:

- no afirmar algo si hay duda razonable
- señalar riesgos y puntos débiles primero
- distinguir claramente entre hecho probado, hecho alegado e inferencia

### 2. Instrucción para atención a clientes

Útil cuando quiere respuestas más claras para compartir con personas no técnicas.

Puede indicar cosas como:

- usar lenguaje simple
- evitar exceso de latinismos
- cerrar con recomendaciones prácticas
- señalar qué falta confirmar antes de actuar

### 3. Instrucción para litigio

Útil cuando quiere que Auri piense como apoyo previo a una estrategia procesal.

Puede indicar cosas como:

- identificar posibles excepciones o defensas
- priorizar carga probatoria
- advertir riesgos de plazo
- sugerir argumentos a favor y en contra

### 4. Instrucción para revisión crítica de argumentos

Útil cuando usted ya tiene una tesis y quiere que Auri la cuestione.

Puede indicar cosas como:

- buscar contraargumentos
- señalar lagunas lógicas
- identificar jurisprudencia que pueda debilitar la posición
- explicar qué puntos requieren mayor sustento

## ¿Dónde se usan?

Las instrucciones se pueden usar en:

- **chats de Auri**
- el paso de razonamiento legal de Auri dentro de ciertos flujos de trabajo

No sirven para redactar en Redactor ni para revisar documentos en Revisiones.

## ¿Qué puede incluir una instrucción?

Normalmente incluye:

- un nombre claro
- una breve descripción
- el texto de las indicaciones
- archivos de apoyo, si necesita dar más contexto

Los archivos pueden servir, por ejemplo, para adjuntar una guía interna, una política de la firma o un documento de referencia que quiera usar siempre con esa instrucción.

## Regla práctica para decidir rápido

Hágase esta pregunta:

**"¿Lo que quiero guardar es una forma de pensar y responder, o una forma de redactar un documento?"**

Si es una forma de pensar y responder, use una **instrucción**.

Si es una forma de redactar, use un **formato**.

## Recomendación final

Empiece con instrucciones sencillas.

No intente meter todo en una sola.

Funciona mejor tener una instrucción clara para cada necesidad frecuente. Por ejemplo:

- una para análisis conservador
- otra para atención a clientes
- otra para revisión crítica

Así le resulta más fácil elegir la adecuada en cada caso.
